/**
 * Types for the Recommendation module API (backend modules/Recommendation).
 *
 * Conventional resource: /api/recommendation-campaign (list, show, store,
 * update, new-form). Action endpoints live under /api/recommendation/…
 * and always take `owner_id` as a query parameter.
 */

export enum RecommendationTypeEnum {
  Repeat = "repeat",
  CrossSell = "cross_sell",
  NewProduct = "new_product",
  ProductGroup = "product_group",
  Manufacturer = "manufacturer",
  Reactivation = "reactivation",
}

export enum RecommendationStatusEnum {
  Active = "active",
  Expired = "expired",
  Suppressed = "suppressed",
  Used = "used",
  Converted = "converted",
}

export enum RecommendationReasonCodeEnum {
  RepeatDue = "repeat_due",
  RepeatOverdue = "repeat_overdue",
  StrongSameOrder = "strong_same_order",
  StrongNextOrder = "strong_next_order",
  StrongLaterOrder = "strong_later_order",
  ProductGroupAffinity = "product_group_affinity",
  ManufacturerAffinity = "manufacturer_affinity",
  NewProductGroupMatch = "new_product_group_match",
  NewProductSimilarCustomers = "new_product_similar_customers",
  ReactivationProduct = "reactivation_product",
}

export enum TriggerTypeEnum {
  RepeatDue = "repeat_due",
  RepeatOverdue = "repeat_overdue",
  CrossSellWindow = "cross_sell_window",
  NewProductMatch = "new_product_match",
  CustomerInactive = "customer_inactive",
  BackInStock = "back_in_stock",
  PriceDrop = "price_drop",
}

export enum TriggerStatusEnum {
  Open = "open",
  Processed = "processed",
  Expired = "expired",
  Suppressed = "suppressed",
}

export enum ProductRelationTypeEnum {
  SameOrder = "same_order",
  NextOrder = "next_order",
  LaterOrder = "later_order",
}

export enum CampaignTypeEnum {
  Repeat = "repeat",
  CrossSell = "cross_sell",
  NewProduct = "new_product",
  Reactivation = "reactivation",
  Mixed = "mixed",
}

export enum CampaignStatusEnum {
  Draft = "draft",
  Active = "active",
  Paused = "paused",
  Finished = "finished",
  Cancelled = "cancelled",
}

export enum CampaignGroupTypeEnum {
  Test = "test",
  Control = "control",
}

export enum CampaignCustomerStatusEnum {
  Selected = "selected",
  Contacted = "contacted",
  Skipped = "skipped",
  Excluded = "excluded",
}

export interface RecommendationProductSummaryResource {
  id: string;
  sku: string | null;
  title: string | null;
  status: string | null;
}

/** The five score components; each in 0–100, weighted into `score`. */
export interface RecommendationScoreComponents {
  base_probability: number | null;
  timing: number | null;
  affinity: number | null;
  relation: number | null;
  confidence: number | null;
}

export interface RecommendationResource {
  id: string;
  customer_id: string;
  product_id: string;
  variant_id: string | null;
  recommendation_type: RecommendationTypeEnum;
  source_product_id: string | null;
  reason_code: RecommendationReasonCodeEnum;
  score: number;
  score_components: RecommendationScoreComponents;
  purchase_probability: number | null;
  /** ISO date (YYYY-MM-DD) */
  expected_purchase_date: string | null;
  valid_from: string | null;
  valid_until: string | null;
  status: RecommendationStatusEnum;
  model_version: string | null;
  calculated_for_date: string | null;
  product?: RecommendationProductSummaryResource;
  source_product?: RecommendationProductSummaryResource;
}

export interface RecommendationTriggerResource {
  id: string;
  customer_id: string;
  recommendation_id: string | null;
  trigger_type: TriggerTypeEnum;
  reference_product_id: string | null;
  trigger_date: string | null;
  priority: number;
  status: TriggerStatusEnum;
  valid_until: string | null;
}

export interface RecommendationProductRelationResource {
  id: string;
  product_from_id: string;
  product_to_id: string;
  relation_type: ProductRelationTypeEnum;
  customer_count: number;
  occurrence_count: number;
  support: number | null;
  confidence: number | null;
  lift: number | null;
  average_days: number | null;
  median_days: number | null;
  standard_deviation_days: number | null;
  relation_score: number | null;
  calculated_for_date: string | null;
  product_to?: RecommendationProductSummaryResource;
}

/**
 * Этап кампании. Вычисляется бэкендом из её состояния и только читается:
 * выбрать его нельзя, поля с таким значением в базе нет.
 */
export enum CampaignStageEnum {
  Draft = "draft",
  Ready = "ready",
  Running = "running",
  Finished = "finished",
}

export interface RecommendationCampaignResource {
  id: string;
  owner_id: string;
  marketing_company_id: string | null;
  name: string;
  campaign_type: CampaignTypeEnum;
  marketplace_id: string | null;
  /** Вычисляемый этап; приходит и в списке, и в одиночном ответе. */
  stage: CampaignStageEnum;
  /** @deprecated Ничем не переводится и ни на что не влияет — читайте `stage`. */
  status: CampaignStatusEnum;
  minimum_score: number;
  test_percentage: number;
  control_percentage: number;
  /** Своё окно замера в днях; null — идём за умолчанием владельца. */
  conversion_window_days: number | null;
  /**
   * Умолчание владельца для окна замера. Приходит только там, где нужно
   * подсказать пустое поле, — в одиночном ответе и в форме создания.
   */
  conversion_window_days_default?: number;
  /**
   * Почему условия кампании больше нельзя менять, или null, если можно.
   * Приходит только в ответе на одиночный запрос, не в списке.
   */
  frozen_reason?: string | null;
  created_at: string | null;
}

/** Body for POST /api/recommendation-campaign */
export interface RecommendationCampaignStoreBody {
  owner_id: string;
  name: string;
  campaign_type: CampaignTypeEnum;
  minimum_score?: number;
  test_percentage: number;
  control_percentage: number;
  conversion_window_days?: number | null;
  randomisation_seed?: string | null;
}

/** Body for PUT /api/recommendation-campaign/{id} — every field optional. */
export type RecommendationCampaignUpdateBody = Partial<
  Omit<RecommendationCampaignStoreBody, "owner_id" | "randomisation_seed"> & {
    marketing_company_id: string | null;
  }
>;

/** GET /api/recommendation-campaign/new-form */
export interface RecommendationCampaignNewFormResource {
  status: CampaignStatusEnum;
  minimum_score: number;
  test_percentage: number;
  control_percentage: number;
  conversion_window_days_default: number;
}

/** POST /api/recommendation/campaigns/{id}/audience?owner_id= */
export interface RecommendationCampaignAudienceResult {
  campaign_id: string;
  newly_assigned: number;
}

/**
 * GET /api/recommendation/campaigns/{id}/customers?owner_id=&group_type=&page=
 *
 * One row of a campaign's audience. `product` is the recommendation the
 * customer was selected for — a control-group row carries it too, and is
 * deliberately never sent it.
 */
export interface RecommendationCampaignCustomerResource {
  id: string;
  customer_id: string;
  customer?: {
    id: string;
    number: string | null;
    name: string | null;
  };
  product?: RecommendationProductSummaryResource;
  group_type: CampaignGroupTypeEnum;
  status: CampaignCustomerStatusEnum;
  score: number | null;
  selection_reason: string | null;
  assigned_at: string | null;
}

/**
 * GET /api/recommendation/campaigns/{id}/uplift?owner_id=
 *
 * Without a control arm the uplift fields are `null`, not 0 — a bare
 * conversion rate must never be shown as an effect.
 */
/**
 * The answer to "did the campaign work" — and the refusal to answer when the
 * sample cannot support one. Four of the seven states mean "no conclusion".
 */
export enum CampaignVerdictEnum {
  NoAudience = "no_audience",
  NotSent = "not_sent",
  NoControlGroup = "no_control_group",
  InsufficientData = "insufficient_data",
  NoDifference = "no_difference",
  Positive = "positive",
  Negative = "negative",
}

export interface RecommendationUpliftResource {
  /** Когорта опыта: кто был в кампании к моменту рассылки. */
  test_customers: number;
  control_customers: number;
  /** Состав кампании целиком, без отсечки по дате рассылки. */
  audience_test_customers: number;
  audience_control_customers: number;
  test_converted: number;
  control_converted: number;
  test_conversion_rate: number | null;
  control_conversion_rate: number | null;
  absolute_uplift: number | null;
  relative_uplift: number | null;
  test_revenue: number;
  control_revenue: number;
  test_revenue_per_customer: number | null;
  control_revenue_per_customer: number | null;
  test_recommended_product_revenue: number;
  incremental_revenue: number | null;
  verdict: CampaignVerdictEnum;
  /** Standard deviations between the arms; null when the test cannot be applied. */
  z_score: number | null;
  /** Smallest difference these group sizes could tell from chance, 0..1. */
  minimum_detectable_difference: number | null;
  /** Связанная маркетинговая кампания, если рассылка шла через неё. */
  marketing_company: {
    id: string;
    name: string;
    /** Сколько получателей в снимке сегмента рассылки. */
    customers_count: number | null;
    /** На какой момент этот снимок посчитан. */
    computed_at: string | null;
    /** Момент закрытия рассылки — его ставит первая отправка. */
    finished_at: string | null;
  } | null;
  /** Момент первого контакта — с него идёт окно измерения. */
  sent_at: string | null;
  /** Длина окна замера в днях: столько считаем покупки после рассылки. */
  conversion_window_days: number;
}
